"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, Brain, HeartHandshake, Moon, Sparkles, Waves, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const symptomsList = [{
  id: 1,
  title: "Stress & Erschöpfung",
  category: "Vitalität",
  description: "Wenn der Alltag Dich überfordert und Deine Energiereserven aufgebraucht sind, schenkt Reiki tiefgehende Erholung und beruhigt Dein überreiztes Nervensystem.",
  iconName: "Activity"
}, {
  id: 2,
  title: "Schlafstörungen & Rastlosigkeit",
  category: "Regeneration",
  description: "Finde zurück zu natürlicher Nachtruhe und innerer Stille, indem kreisende Gedanken sanft gelöst und körperliche Spannungszustände abgebaut werden.",
  iconName: "Moon"
}, {
  id: 3,
  title: "Emotionale Blockaden",
  category: "Emotionale Ebene",
  description: "Unterdrückte Gefühle und alte Wunden können sanft ins Bewusstsein geholt und mit heilsamer Zuwendung und Mitgefühl aufgelöst werden.",
  iconName: "HeartHandshake"
}, {
  id: 4,
  title: "Ängste & Innere Unruhe",
  category: "Seelisches Wohlbefinden",
  description: "Reiki stärkt Dein Urvertrauen und schenkt Dir ein wohltuendes Gefühl von Geborgenheit, Halt und tiefer Zentrierung in herausfordernden Lebensphasen.",
  iconName: "Waves"
}, {
  id: 5,
  title: "Körperliche Verspannungen",
  category: "Körperliche Ebene",
  description: "Chronische Muskelverspannungen im Nacken-, Schulter- und Rückenbereich können sich durch den harmonisierenden Energiefluss spürbar lockern.",
  iconName: "Sparkles"
}, {
  id: 6,
  title: "Konzentrationsschwäche & Mentaler Nebel",
  category: "Mentale Klarheit",
  description: "Klärung Deines Geistes für mehr Fokus und Entscheidungsfreude. Die Energiebehandlung fördert Frische, Weitsicht und intuitive Einsichten.",
  iconName: "Brain"
}];
export default function Symptoms() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (name: string) => {
    const iconProps = {
      className: "size-5 text-primary"
    };
    switch (name) {
      case "Activity":
        return <Activity {...iconProps} />;
      case "Moon":
        return <Moon {...iconProps} />;
      case "HeartHandshake":
        return <HeartHandshake {...iconProps} />;
      case "Waves":
        return <Waves {...iconProps} />;
      case "Sparkles":
        return <Sparkles {...iconProps} />;
      case "Brain":
        return <Brain {...iconProps} />;
      default:
        return <Sparkles {...iconProps} />;
    }
  };
  return <section id="reikisymptomshelp" data-nav="light" className="relative bg-background py-20 md:py-28 lg:py-32 overflow-hidden">
      {}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-accent/40 rounded-3xl blur-3xl pointer-events-none -z-10" />
      <div className="absolute bottom-10 right-10 w-72 h-72 bg-secondary/30 rounded-3xl blur-2xl pointer-events-none -z-10" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }}>
            <Badge variant="secondary" className="mb-4 px-3 py-1 font-medium text-xs tracking-wider uppercase bg-secondary text-secondary-foreground border-border/50">
              Ganzheitliche Begleitung
            </Badge>
          </motion.div>

          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.6,
          delay: 0.1,
          ease: "easeOut"
        }} className="font-serif text-3xl sm:text-4xl md:text-5xl font-normal text-foreground tracking-tight leading-tight mb-6">
            Bei welchen Symptomen hilft Reiki?
          </motion.h2>

          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.6,
          delay: 0.2,
          ease: "easeOut"
        }} className="text-base sm:text-lg text-foreground/80 leading-relaxed font-sans font-normal">
            Reiki regt Deine natürlichen Selbstheilungskräfte sanft an und bringt Körper, Geist und Seele wieder in ein harmonisches Gleichgewicht. Auf körperlicher, emotionaler und mentaler Ebene unterstützt Dich die achtsame Energiearbeit dabei, alte Spannungen loszulassen und neue Lebenskraft zu schöpfen.
          </motion.p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {symptomsList.map((item, i) => <div key={item.id} data-index={i} className="h-full">
              <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.5,
            delay: i * 0.08,
            ease: "easeOut"
          }} className="h-full">
                <Card className="h-full bg-card border border-border/70 rounded-xl shadow-sm hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] duration-300 flex flex-col justify-between">
                  <div>
                    <CardHeader className="flex flex-row items-start justify-between gap-4 pb-4">
                      <div className="size-11 rounded-lg bg-secondary/80 flex items-center justify-center shrink-0 border border-border/40">
                        {getIcon(item.iconName)}
                      </div>
                      <Badge variant="outline" className="text-[11px] font-medium border-border/60 text-foreground/75 bg-background/50">
                        {item.category}
                      </Badge>
                    </CardHeader>

                    <CardContent className="space-y-2.5">
                      <CardTitle className="font-serif text-xl sm:text-2xl text-foreground font-medium leading-snug">
                        {item.title}
                      </CardTitle>
                      <CardDescription className="text-foreground/75 text-sm sm:text-base leading-relaxed font-sans">
                        {item.description}
                      </CardDescription>
                    </CardContent>
                  </div>

                  <div className="px-6 pb-6 pt-2">
                    <div className="h-px w-full bg-border/40" />
                  </div>
                </Card>
              </motion.div>
            </div>)}
        </div>

        {}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.6,
        delay: 0.3,
        ease: "easeOut"
      }} className="mt-14 md:mt-20 p-8 sm:p-10 rounded-2xl bg-muted/70 border border-border/80 text-center flex flex-col items-center justify-center max-w-3xl mx-auto shadow-xs">
          <h3 className="font-serif text-2xl sm:text-3xl text-foreground mb-3 font-normal">
            Bist Du bereit für Deine persönliche Transformation?
          </h3>
          <p className="text-sm sm:text-base text-foreground/80 mb-6 max-w-xl font-sans">
            Jeder Heilungsweg ist individuell. In einer geschützten und ruhigen Atmosphäre begleite ich Dich achtsam auf Deinem Weg zu mehr innerer Ruhe und Vitalität.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto justify-center">
            <Button asChild size="lg" className="w-full sm:w-auto font-medium gap-2 shadow-sm">
              <Link to="/ContactPage">
                <span>Einzelsitzung anfragen</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/ReikiTherapyPage">
                Mehr über Reiki erfahren
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}